import React, { createElement } from "react";
import { useWindowDimensions, Platform } from "react-native";
import RenderHtml, { RenderHTMLProps } from "react-native-render-html";
import { sanitizeHtmlByDescriptionFormat } from "../../../../utils/sanitizeHtml";
import { useIframeAllowedDomains } from "../../../../hooks/useIframeAllowedDomains";

interface CustomHtmlProps extends Partial<RenderHTMLProps> {
  html: string;
  /** When true, renders via dangerouslySetInnerHTML on web so iframes (YouTube, CodePen, etc.) work. Caller MUST ensure html is pre-sanitized. */
  allowEmbeds?: boolean;
}

const CustomHtml: React.FC<CustomHtmlProps> = ({ html, allowEmbeds = false, ...props }) => {
  const { width } = useWindowDimensions();
  const iframeDomainAllowlist = useIframeAllowedDomains();

  if (Platform.OS === "web" && allowEmbeds && html) {
    return createElement("div", {
      "data-testid": "custom-html-with-embeds",
      dangerouslySetInnerHTML: { __html: sanitizeHtmlByDescriptionFormat("HTML", html, iframeDomainAllowlist) },
      style: {
        fontFamily: "Inter, system-ui, sans-serif",
        fontSize: 16,
        lineHeight: "24px",
        color: "#344054",
      },
    });
  }

  return (
    <RenderHtml
      contentWidth={width}
      source={{ html: sanitizeHtmlByDescriptionFormat("HTML", html, iframeDomainAllowlist) ?? "" }}
      tagsStyles={customStyles}
      {...props}
    />
  );
};

const customStyles = {
  body: {
    fontFamily: "Inter",
    fontSize: 16,
    color: "#344054",
    lineHeight: 24,
    paragraphSpacing: 16,
  },
  h1: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#344054",
  },
  p: {
    fontFamily: "Inter",
    fontSize: 16,
    lineHeight: 24,
    color: "#344054",
    paragraphSpacing: 16,
  },
  a: {
    color: "#1e90ff",
    textDecorationLine: "underline",
  },
  code: {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
    fontSize: 14,
    padding: 2,
    paddingRight: 4,
    borderRadius: 4,
    color: "#111827",
    backgroundColor: "#f3f4f6",
  },
  pre: {
    backgroundColor: "#0b1220",
    color: "#e5e7eb",
    borderRadius: 8,
    padding: 12,
  },
  "pre code": {
    color: "#e5e7eb",
    backgroundColor: "transparent",
    padding: 0,
  },

  blockquote: {
    fontStyle: "italic",
    backgroundColor: "#f8fafc",
    borderColor: "#3b82f6",
    borderLeftWidth: 4,
    marginHorizontal: 10,
    paddingHorizontal: 10,
    borderRadius: 6,
  },
  "blockquote p": {
    margin: 0,
    fontStyle: "italic",
  },
} as const;

export default CustomHtml;
